import { Teleport, Transition, defineComponent, ref, onMounted } from 'vue'

import { useAnalytics } from '#Analytics/composables/useAnalytics'
import { useConsent } from '#Analytics/composables/useConsent'
import { UButton } from '#components'

export interface useCookieModalConfig {
  delay?: number
}

const DEFAULT_BANNER_TEXT
  = 'Мы используем файлы cookie, чтобы улучшить работу сайта и сделать его '
    + 'удобнее. «Принять все» — включая аналитические cookies; '
    + '«Только необходимые» — без них.'

/**
 * Баннер согласия на cookies (CMP, ТЗ аналитики §11.2): категории
 * «необходимые / аналитика+маркетинг», текст и ссылка на политику —
 * из cmp-конфига витрины. Показывается, только если CMP включён на канале
 * и актуального решения нет (с учётом TTL)
 */
export const useCookieModal = (config: useCookieModalConfig) => {
  const isCookieModalOpen = ref(false)

  function onAcceptAll() {
    const { acceptAll } = useConsent()
    acceptAll()
    isCookieModalOpen.value = false
  }

  function onNecessaryOnly() {
    const { acceptNecessaryOnly } = useConsent()
    acceptNecessaryOnly()
    isCookieModalOpen.value = false
  }

  const modal = defineComponent({
    setup() {
      const { store } = useAnalytics()

      return () => {
        const cmp = store.config?.cmp
        const bannerText = cmp?.bannerText || DEFAULT_BANNER_TEXT
        const policyUrl = cmp?.privacyPolicyUrl

        return (
          <Teleport to="body">
            <Transition
              enterActiveClass="transition duration-500 ease-out"
              enterFromClass="translate-y-full opacity-0"
              enterToClass="translate-y-0 opacity-100"
              leaveActiveClass="transition duration-300 ease-in"
              leaveFromClass="translate-y-0 opacity-100"
              leaveToClass="translate-y-full opacity-0"
            >
              {isCookieModalOpen.value && (
                <div class="fixed right-1 bottom-2 left-1 z-50 m-auto max-w-2xl">
                  <div class="flex flex-col gap-3 border-2 border-neutral-600 bg-white/90 p-3 shadow-2xl backdrop-blur-md">
                    <div class="text-sm leading-relaxed text-slate-700 md:text-base">
                      {bannerText}
                      {policyUrl && (
                        <>
                          {' '}
                          <a
                            href={policyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="underline"
                          >
                            Политика конфиденциальности
                          </a>
                        </>
                      )}
                    </div>
                    <div class="flex flex-col gap-2 md:flex-row md:justify-end">
                      <UButton
                        // @ts-ignore
                        color="neutral"
                        variant="outline"
                        class="w-full shrink-0 justify-center md:w-auto"
                        onClick={onNecessaryOnly}
                      >
                        Только необходимые
                      </UButton>
                      <UButton
                        // @ts-ignore
                        color="tertiary"
                        class="w-full shrink-0 justify-center md:w-auto"
                        onClick={onAcceptAll}
                      >
                        Принять все
                      </UButton>
                    </div>
                  </div>
                </div>
              )}
            </Transition>
          </Teleport>
        )
      }
    },
  })

  onMounted(async () => {
    const { support } = useAnalytics()
    const { restore, bannerRequired } = useConsent()

    // Конфиг CMP нужен до решения о показе (включённость баннера, TTL)
    await support.loadConfig().catch(() => undefined)
    restore()

    if (!bannerRequired()) return

    setTimeout(() => {
      isCookieModalOpen.value = true
    }, config.delay ?? 1e3)
  })

  return {
    cookieModal: modal,
  }
}
